import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ isCollapsed = false, onToggle, isMobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const { user, userProfile } = useAuth();

  useEffect(() => {
    setMounted(true);
  }, []);

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: '/dashboard-overview', 
      icon: 'LayoutDashboard',
      description: 'Visão geral dos lucros e métricas'
    },
    { 
      label: 'Impressoras', 
      path: '/printer-management', 
      icon: 'Printer',
      description: 'Gerenciar impressoras 3D'
    },
    { 
      label: 'Filamentos', 
      path: '/filament-inventory', 
      icon: 'Filament',
      description: 'Controle de estoque de materiais'
    },
    { 
      label: 'Marketplaces', 
      path: '/marketplace-management', 
      icon: 'ShoppingCart',
      description: 'Cadastre plataformas e taxas de venda'
    },
    { 
      label: 'Calculadora', 
      path: '/calculator', 
      icon: 'Calculator',
      description: 'Escolha entre rápido ou profissional'
    },
    { 
      label: 'Cálculo Rápido',
      path: '/quick-calculator',
      icon: 'Zap',
      description: 'Estimativa rápida com parâmetros essenciais'
    },
    { 
      label: 'Histórico',
      path: '/historico',
      icon: 'Projects',
      description: 'Projetos utilizados e arquivos anexos'
    },
    {
      label: 'Configurações',
      path: '/configuracoes',
      icon: 'Settings',
      description: 'Gerenciar fornecedores, materiais e marcas'
    }
  ];

  const isActivePath = (path) => location?.pathname === path;

  if (!mounted) return null;

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          glass-panel fixed top-16 left-0 h-[calc(100vh-4rem)] z-50
          transition-all duration-300 ease-in-out
          lg:top-16 lg:h-[calc(100vh-4rem)]
          sm:top-14 sm:h-[calc(100vh-3.5rem)]
          ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}
          ${isMobileOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Toggle Button - Desktop Only */}
          <div className="hidden lg:flex justify-end p-4 border-b border-border">
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="hover:bg-muted transition-colors"
              aria-label={isCollapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
            >
              <Icon 
                name={isCollapsed ? "ChevronRight" : "ChevronLeft"} 
                size={16} 
                className="text-muted-foreground"
              />
            </Button>
          </div>

          {/* User Profile Section */}
          {!isCollapsed && (
            <div className="px-4 py-3 border-b border-border">
              {user ? (
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center shadow-lg">
                    <Icon name="User" size={18} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {userProfile?.full_name || 'Usuário'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {userProfile?.company_name || 'Fabricante 3D'}
                    </p>
                  </div>
                </div>
              ) : (
                <Link 
                  to="/authentication-login"
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Icon name="LogIn" size={18} />
                  <span className="text-sm font-medium">Fazer Login</span>
                </Link>
              )}
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigationItems?.map((item) => {
              const isActive = isActivePath(item?.path);
              
              return (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={onMobileClose}
                  className={`nav-item ${isActive ? 'nav-item-active' : ''} ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
                  title={isCollapsed ? item?.label : ''}
                >
                  <Icon 
                    name={item?.icon} 
                    size={20} 
                    variant={['Printer','Filament','Projects'].includes(item?.icon) ? 'masked' : 'lucide'}
                    className={`flex-shrink-0 ${isActive ? 'text-primary-foreground' : 'text-muted-foreground'}`}
                  />
                  <div className={`flex-1 min-w-0 ${isCollapsed ? 'lg:hidden' : ''}`}>
                    <div className="font-medium truncate">{item?.label}</div>
                    <div className={`text-xs opacity-70 truncate ${isActive ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                      {item?.description}
                    </div>
                  </div>
                  
                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="hidden lg:group-hover:block absolute left-full ml-2 px-3 py-2 bg-popover text-popover-foreground text-sm rounded-lg shadow-elevation-2 whitespace-nowrap z-60">
                      <div className="font-medium">{item?.label}</div>
                      <div className="text-xs text-muted-foreground">{item?.description}</div>
                    </div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div className={`p-4 border-t border-border ${isCollapsed ? 'lg:px-2' : ''}`}>
            <div className={`flex items-center space-x-3 text-xs text-muted-foreground ${isCollapsed ? 'lg:justify-center' : ''}`}>
              <Icon name="Zap" size={16} className="flex-shrink-0 text-primary" />
              <span className={isCollapsed ? 'lg:hidden' : ''}>
                FioGo v1.0
              </span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;